import { Container } from 'components/box';
import {
  PageProps,
  PermanentShopEnum,
  ProductInterface,
  SoldOutHeaderInterface,
} from 'types/types';
import { QueryProductArgs, QueryProductOutput } from 'types/types';
import { useCustomerContext, useStaffPreview } from 'hooks/customer';
import { useLazyQuery } from '@apollo/client';
import Head from 'next/head';
import ProductDetails from 'components/_pages/products';
import productQuery from 'queries/product.graphql';
import NotFoundPage from 'pages/404';
import { memo, useEffect } from 'react';
import Loading from 'components/loading';
import { useErrorNotificationHandler } from 'hooks/handle-errors';
import {
  fetchGraphQl,
  parseQueryAST,
  isAbortError,
  isNotFound,
} from '../../util/graphql';
import config from '../../util/load-config';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { AlgoliaIndexEnum } from 'types/interface';
import { getProductTitle, prepMetaTags } from '../../util/seo';
import { canonicalUrl, stringifyQueryParam } from '../../util/url';
import { logError } from '../../util/error-logger';
import Schema from 'components/_global/schema';
import { mapProductSchema } from 'components/_global/schema/util';
import dynamic from 'next/dynamic';
import { mapProductToPageTitle } from '../../util/tag-manager';
import { cacheControl } from 'constants/cache-control';
import { logInvalidJSON } from '../../util/log-invalid-json';
import findPermanentShopIndex from '../../util/find-permanent-shop-index';

const StaffDealPreview = dynamic(
  () => import('components/_pages/products/staff-preview')
);

const Meta = ({ product }: { product: ProductInterface }) => (
  <>
    <Head>
      {(product.meta || []).map(meta => {
        const props = { [meta.attribute.name]: meta.attribute.value };
        return (
          <meta
            key={`${meta.attribute.name}.${meta.attribute.value}`}
            content={meta.content}
            {...props}
          />
        );
      })}
    </Head>
    <Schema schema={mapProductSchema(product)} />
  </>
);

const Product = memo(
  ({
    product,
    soldOutHeaders,
  }: {
    product: ProductInterface;
    soldOutHeaders?: SoldOutHeaderInterface[];
  }) => (
    <>
      <Meta product={product} />
      <Container>
        <ProductDetails product={product} soldOutHeaders={soldOutHeaders} />
      </Container>
    </>
  )
);

interface SharedProductProps {
  product?: ProductInterface;
  soldOutHeaders?: SoldOutHeaderInterface[];
}

type ProductPageProps = SharedProductProps & {
  id: string;
};

type ProductWrapperProps = SharedProductProps & {
  id?: string;
};

const ProductPage = ({
  id,
  product: productSSR,
  soldOutHeaders,
}: ProductPageProps) => {
  const context = useCustomerContext();
  const mountErrors = useErrorNotificationHandler();

  const [previewProduct, { loading, data, error }] = useLazyQuery<
    QueryProductOutput,
    QueryProductArgs
  >(productQuery, {
    variables: { id },
    context,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  useStaffPreview(({ date }) => previewProduct({ variables: { id, p: date } }));

  useEffect(() => {
    error && mountErrors([new Error('Preview failed')]);
  }, [error, mountErrors]);

  const product = (data && data.product) || productSSR;

  if (!product) {
    return (
      <Loading
        iconTop={200}
        isLoading={loading}
        loadingText="Preview loading..."
      >
        <NotFoundPage />
      </Loading>
    );
  }

  return (
    <Loading iconTop={200} isLoading={loading} loadingText="Preview loading...">
      <Product product={product} soldOutHeaders={soldOutHeaders} />
      {data && data.product && <StaffDealPreview product={data.product} />}
    </Loading>
  );
};

const ProductWrapper = ({ id, ...restProps }: ProductWrapperProps) => {
  if (!id) return <NotFoundPage />;
  return <ProductPage id={id} {...restProps} />;
};

export async function getServerSideProps({
  query: { id: idParam },
  res,
  resolvedUrl,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<ProductWrapperProps & PageProps>
> {
  const id = stringifyQueryParam(idParam);
  if (!id) {
    res.setHeader('cache-control', cacheControl.temporary);

    res.statusCode = 404;
    logError('Missing product ID', 'products.[id].undefined}');

    // return empty props so we can render a failure page
    return { props: {} };
  }

  const canonical = canonicalUrl(resolvedUrl);

  try {
    const [query, operationName] = await parseQueryAST(productQuery, 'product');

    const { data, errors } = await fetchGraphQl<
      QueryProductOutput,
      QueryProductArgs
    >({
      operationName,
      query,
      variables: { id },
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.product) {
      res.setHeader('cache-control', cacheControl.temporary);

      // log error and set status based on error and data
      if (errors && errors.length > 0) {
        const [firstError] = errors;
        logError(firstError.message, `products.query.${id}`);

        if (isNotFound(firstError)) {
          res.statusCode = 404;
        } else {
          res.statusCode = 500;
        }
      } else {
        res.statusCode = 404;
        logError(
          `No data returned for product: "${id}"`,
          `products.query.${id}`
        );
      }

      // return empty props so we can render a failure page
      return { props: { id } };
    }

    // go ahead
    res.setHeader('cache-control', cacheControl.dynamic);

    const isClearance = data.product.isClearanceSale;
    const activeShop = data.product.permanentShop?.id;
    const activePage = isClearance
      ? 'clearance-sale'
      : activeShop
      ? activeShop
      : null;

    const headerTaglineTitle =
      activePage && config.headerTaglines[activePage]?.header;
    const headerTaglineText =
      activePage && config.headerTaglines[activePage]?.text;

    const productActiveToDate = data.product.activeToDate
      ? new Date(data.product.activeToDate).getTime()
      : 0;
    const cuttOffDate = new Date(config.seo.noIndexCuttOffDate).getTime();
    const noIndex = cuttOffDate > productActiveToDate;

    const meta = config.seo.meta.pages['products'];
    const description = [
      data.product.brand,
      data.product.name,
      prepMetaTags(meta).description,
    ].join(' ');

    const searchIndex = isClearance
      ? AlgoliaIndexEnum.Clearance
      : !activeShop
      ? AlgoliaIndexEnum.Homepage
      : findPermanentShopIndex(activeShop);

    return {
      props: {
        id,
        product: data.product,
        soldOutHeaders: data.configs?.soldOutOverlay,
        meta: {
          ...(isClearance
            ? { theme: 'clearancesale' }
            : activeShop === PermanentShopEnum.EXTRA_TIME_DEALS
            ? { theme: 'extratimedeals' }
            : activeShop === PermanentShopEnum.EVERYDAY_ESSENTIALS
            ? { theme: 'everydayessentials' }
            : activeShop === 'gift-vouchers'
            ? { theme: 'giftvouchers' }
            : {}),
          isAnotherDayOnlyActive: data.configs?.anotherDayOnly?.isActive,
          messages: data.messages || null,
        },
        layout: {
          gtmPageTitle: mapProductToPageTitle(data.product),
          metaTags: {
            title: getProductTitle(data.product),
            ...(canonical ? { canonical } : {}),
            noIndex,
            ...(meta ? { description } : {}),
          },
          selectedTopTab: isClearance
            ? 'clearancesale'
            : activeShop
            ? activeShop
            : 'home',
          ...(searchIndex ? { searchIndexName: searchIndex } : {}),
          ...(activePage
            ? {
                hideClock: true,
                showAdoHeader: false,
                ...(headerTaglineTitle ? { headerTaglineTitle } : {}),
                ...(headerTaglineText ? { headerTaglineText } : {}),
              }
            : {}),
        },
      },
    };
  } catch (e) {
    res.statusCode = isAbortError(e) ? 503 : 500;
    res.setHeader('cache-control', cacheControl.temporary);

    logError(e, `products.query.${id}`);

    // return empty props so we can render a failure page
    return { props: { id } };
  }
}

export default ProductWrapper;
